import type { UserFilters } from '~/pages/backoffice/admin/user/list/use-user-filters'
import type { useTableQuery } from '~/pages/backoffice/useTableQuery'
import { useQuery } from '@apollo/client/react'
import { GQL_USERS_WITH_FILTERS } from '~/gql/users/users'
import { lastPage } from '~/pages/backoffice/useTableQuery'

type TableQuery = Pick<ReturnType<typeof useTableQuery>, 'page' | 'pageSize' | 'setPage' | 'queryArgs'>

/**
 * La page de comptes affichée, lue côté serveur avec la recherche et les filtres.
 *
 * Pendant qu'une autre page charge, la précédente reste à l'écran (`previousData`) :
 * le tableau et son pied paginé ne se démontent pas à chaque clic, et leur région
 * de statut reste celle qui annonce le nombre de résultats.
 */
export function useUsersPage({ page, pageSize, setPage, queryArgs }: TableQuery, { query, filterBy }: Pick<UserFilters, 'query' | 'filterBy'>) {
  const { data: current, previousData, refetch, loading } = useQuery(GQL_USERS_WITH_FILTERS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: { input: { ...queryArgs, searchInput: query, filterBy } },
  })
  const shown = (current ?? previousData)?.getFilteredUsers
  const total = shown?.total ?? 0

  // Page vidée sous les pieds (son dernier compte supprimé, ou retiré du groupe
  // filtré) : revenir à la dernière page qui existe, sans quoi l'écran disait
  // « aucun utilisateur » sans pagination pour en sortir.
  if (current && page > lastPage(total, pageSize)) setPage(lastPage(total, pageSize))

  return {
    users: shown?.users ?? [],
    total,
    /** Une réponse est arrivée : avant, il n'y a rien à montrer qu'un indicateur de chargement. */
    loaded: shown !== undefined,
    loading,
    refetch,
  }
}
